import { PropertyResponseDto } from "@/common/api/datasources/Api";
import { usePropertiesQuery } from "@/common/api/services/properties";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import {
    Building2,
    Home,
    MapPin,
    Building,
    DollarSign,
    Ruler,
    Hash
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const AVATAR_SIZES = {
    xs: "h-8 w-8",
    sm: "h-10 w-10",
    md: "h-12 w-12",
    lg: "h-16 w-16",
    xl: "h-24 w-24",
} as const;

type AvatarSize = keyof typeof AVATAR_SIZES;

interface PropertyTooltipProps {
    property?: PropertyResponseDto;
    propertyUUID?: string;
    size?: AvatarSize;
    showDetails?: boolean;
    disableNavigation?: boolean;
}

const LoadingTooltip = ({ size = "md" }: { size: AvatarSize }) => {
    return (
        <div className="flex items-center gap-3 p-2">
            <Skeleton className={cn(AVATAR_SIZES[size], "rounded-lg")} />
            <div className="space-y-2">
                <Skeleton className="h-4 w-[120px]" />
                <Skeleton className="h-3 w-[160px]" />
            </div>
        </div>
    );
};

const PropertyTooltipContent = ({
    property,
    size = "md",
    showDetails = true,
    disableNavigation = false
}: {
    property: PropertyResponseDto;
    size: AvatarSize;
    showDetails?: boolean;
    disableNavigation?: boolean;
}) => {
    const { t } = useTranslation();

    const content = (
        <div className="flex items-center gap-3 cursor-pointer hover:bg-muted/50 rounded-md p-2 transition-all duration-200 group">
            <Avatar className={cn(
                AVATAR_SIZES[size],
                "rounded-lg ring-2 ring-offset-2 ring-transparent transition-all duration-200",
                "group-hover:ring-primary/20"
            )}>
                {property.images?.[0]?.thumbnail?.url ? (
                    <AvatarImage
                        src={property.images[0].thumbnail.url}
                        alt={property.name}
                        className="object-cover rounded-lg"
                    />
                ) : null}
                <AvatarFallback className="bg-primary/5 text-primary rounded-lg">
                    <Building2 className="w-4 h-4" />
                </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
                <span className="font-medium group-hover:text-primary transition-colors">
                    {property.name}
                </span>
                <span className="text-sm text-muted-foreground">
                    {property.address || property.city}
                </span>
            </div>
        </div>
    );

    return (
        <HoverCard>
            <HoverCardTrigger asChild>
                {disableNavigation ? (
                    content
                ) : (
                    <Link to={`/home/properties/${property.uuid}`}>
                        {content}
                    </Link>
                )}
            </HoverCardTrigger>
            {showDetails && (
                <HoverCardContent className="w-80">
                    <div className="space-y-4">
                        <div className="flex items-start gap-3 pb-3 border-b">
                            <Avatar className={cn(AVATAR_SIZES[size], "rounded-lg")}>
                                {property.images?.[0]?.thumbnail?.url ? (
                                    <AvatarImage
                                        src={property.images[0].thumbnail.url}
                                        alt={property.name}
                                        className="object-cover rounded-lg"
                                    />
                                ) : null}
                                <AvatarFallback className="bg-primary/5 text-primary rounded-lg">
                                    <Building2 className="w-6 h-6" />
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col flex-1">
                                <span className="font-semibold text-lg">{property.name}</span>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <MapPin className="w-3 h-3" />
                                    {property.address || property.city}
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm">
                                <Home className="w-4 h-4 text-muted-foreground" />
                                <span className="text-muted-foreground">{t('properties.form.type')}</span>
                                <span className="ml-auto font-medium">
                                    {t(`properties.type.${property.type.toLowerCase()}`)}
                                </span>
                            </div>

                            {property.totalArea && (
                                <div className="flex items-center gap-2 text-sm">
                                    <Ruler className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">{t('properties.form.totalArea')}</span>
                                    <span className="ml-auto font-medium">
                                        {property.totalArea} m²
                                    </span>
                                </div>
                            )}

                            {property.monthlyPrice && (
                                <div className="flex items-center gap-2 text-sm">
                                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">{t('properties.form.monthlyPrice')}</span>
                                    <span className="ml-auto font-medium">
                                        ${property.monthlyPrice}
                                    </span>
                                </div>
                            )}

                            {property.rooms && (
                                <div className="flex items-center gap-2 text-sm">
                                    <Building className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">{t('properties.form.rooms')}</span>
                                    <span className="ml-auto font-medium">
                                        {property.rooms}
                                    </span>
                                </div>
                            )}

                            {property.propertyCode && (
                                <div className="flex items-center gap-2 text-sm">
                                    <Hash className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">{t('properties.form.propertyCode')}</span>
                                    <span className="ml-auto font-medium">
                                        {property.propertyCode}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </HoverCardContent>
            )}
        </HoverCard>
    );
};

const NoPropertyContent = ({ size = "md" }: { size: AvatarSize }) => {
    const { t } = useTranslation();

    return (
        <div className="flex items-center gap-3 p-2">
            <Avatar className={cn(
                AVATAR_SIZES[size],
                "bg-muted rounded-lg"
            )}>
                <AvatarFallback>
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
                <span className="text-muted-foreground font-medium">
                    {t("common.propertyTooltip.noProperty")}
                </span>
                <span className="text-sm text-muted-foreground/70">
                    {t("common.propertyTooltip.propertyRemoved")}
                </span>
            </div>
        </div>
    );
};

export const PropertyTooltip = Object.assign(
    ({
        property,
        propertyUUID,
        size = "md",
        showDetails = true,
        disableNavigation = false
    }: PropertyTooltipProps) => {
        const propertiesQuery = usePropertiesQuery();
        const { data: fetchedProperty, isLoading } = useQuery({
            ...propertiesQuery.propertyByIdQuery(propertyUUID || ""),
            enabled: !!propertyUUID && !property
        });

        if (isLoading) {
            return <LoadingTooltip size={size} />;
        }

        const propertyData = property || fetchedProperty;

        if (!propertyData && !propertyUUID) {
            return <NoPropertyContent size={size} />;
        }

        if (!propertyData) {
            return null;
        }

        return (
            <PropertyTooltipContent
                property={propertyData}
                size={size}
                showDetails={showDetails}
                disableNavigation={disableNavigation}
            />
        );
    },
    {
        size: {
            xs: "xs",
            sm: "sm",
            md: "md",
            lg: "lg",
            xl: "xl"
        } as const
    }
); 